// Lohnanteil eines Belegs (BUT-0030, 29.10.2026) – EINE Regel für Seitenleiste, Summe im Beleg, Rückfrage beim Drucken/Senden.
// Gleiche Regel im Server: backend/utils/lohnanteil.py (beide mit tests/simulation/simulation_but0029_0030.py geprüft).
//
//   Leistung      → ganze Position netto (Menge × Einzelpreis, Belegrabatt anteilig) ist Lohn;
//                   steht beim Artikel ein Lohn, dann Lohn × Menge
//   Fremdleistung → Lohn des Artikels × Menge
//   Artikel (Material) → 0
//   freie Position (kein Artikel) → 0 mit Hinweis „Lohn bitte prüfen“ (steht schon ein Lohn in der Position, bleibt er)
//   Hand geht vor → Position mit labor_cost_manuell: der von Ralph eingetragene Wert, nie von der Automatik überschrieben
// Der Artikeltyp steht in der Position (artikel_typ); alte Belege ohne Typ: über source_article_id aus dem Artikel nachgeschlagen (nur lesen).

export const runde2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
const zahl = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0; };

export const ARTIKEL_TYP_LEISTUNG = "Leistung";
export const ARTIKEL_TYP_FREMD = "Fremdleistung";
export const ARTIKEL_TYP_MATERIAL = "Artikel";

/** Zeilen, die Geld kosten (keine Titel, Texte, leeren Zeilen). */
export const istLeistungszeile = (p) => (p?.type || "position") === "position" && !!(p?.description || "").trim();

export const artikelTypVon = (pos, artikelListe = []) => {
  if (pos?.artikel_typ) return pos.artikel_typ;
  if (pos?.source_article_id) return (artikelListe.find((a) => a.id === pos.source_article_id) || {}).typ || "";
  return "";
};

/** Faktor für den Belegrabatt (Netto nach Rabatt / Netto vor Rabatt). */
export const rabattFaktor = (subtotal, netAfterDiscount) => (zahl(subtotal) > 0 ? zahl(netAfterDiscount) / zahl(subtotal) : 1);

/** Lohn einer Position: { betrag, art, pruefen }. art: leistung | fremd | material | frei | hand */
export const lohnVonPosition = (pos, artikelListe = [], faktor = 1) => {
  const menge = zahl(pos.quantity);
  const lc = zahl(pos.labor_cost);
  if (pos.labor_cost_manuell === true) return { betrag: runde2(menge > 0 ? lc * menge : lc), art: "hand", pruefen: false };
  const typ = artikelTypVon(pos, artikelListe);
  if (typ === ARTIKEL_TYP_LEISTUNG) {
    return { betrag: runde2(lc > 0 ? lc * menge : menge * zahl(pos.price_net) * faktor), art: "leistung", pruefen: false };
  }
  if (typ === ARTIKEL_TYP_FREMD) return { betrag: runde2(lc * menge), art: "fremd", pruefen: lc <= 0 };
  if (typ === ARTIKEL_TYP_MATERIAL) return { betrag: 0, art: "material", pruefen: false };
  return { betrag: runde2(lc > 0 ? lc * menge : 0), art: "frei", pruefen: lc <= 0 };
};

/** Alle Zeilen für die Seitenleiste: [{ index (in positions), pos, betrag, art, pruefen }] */
export const lohnZeilen = (positions = [], artikelListe = [], faktor = 1) =>
  positions.map((pos, index) => ({ pos, index })).filter(({ pos }) => istLeistungszeile(pos))
    .map(({ pos, index }) => ({ index, pos, ...lohnVonPosition(pos, artikelListe, faktor) }));

export const lohnSumme = (zeilen) => runde2(zeilen.reduce((s, z) => s + z.betrag, 0));

/** Kurzfassung für die Rückfrage: „3 Leistungen, 1 Fremdleistung“ */
export const lohnBeschreibung = (zeilen) => {
  const n = (art) => zeilen.filter((z) => z.art === art && z.betrag > 0).length;
  const teile = [];
  const l = n("leistung"), f = n("fremd"), h = n("hand"), fr = n("frei");
  if (l) teile.push(`${l} ${l === 1 ? "Leistung" : "Leistungen"}`);
  if (f) teile.push(`${f} ${f === 1 ? "Fremdleistung" : "Fremdleistungen"}`);
  if (h) teile.push(`${h} von Hand`);
  if (fr) teile.push(`${fr} sonstige`);
  return teile.join(", ");
};

/** Neuer Wert aus der Seitenleiste (Gesamtbetrag der Position) → Position, von Hand gesetzt (Hand geht vor). */
export const positionMitHandLohn = (pos, gesamtBetrag) => {
  const menge = zahl(pos.quantity);
  const gesamt = zahl(gesamtBetrag);
  return { ...pos, labor_cost: menge > 0 ? Math.round((gesamt / menge) * 10000) / 10000 : gesamt, labor_cost_manuell: true };
};

/** Zurück zur Automatik: Lohn wieder aus dem Artikel (nichts Eigenes). */
export const positionAutomatisch = (pos, artikelListe = []) => {
  const art = pos.source_article_id ? artikelListe.find((a) => a.id === pos.source_article_id) : null;
  const rest = { ...pos };
  delete rest.labor_cost_manuell;
  return { ...rest, labor_cost: art ? zahl(art.labor_cost) : 0 };
};
